// A trailing `Z` or an explicit `±HH:MM` / `±HHMM` offset — the only cases
// where the instant is unambiguous and safe to re-express in Eastern time.
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

// Fixed to America/New_York so every analyst sees the same wall-clock time
// regardless of their machine's locale. MM/DD/YYYY h:mm AM/PM.
const EST_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/**
 * Formats a BFF timestamp as `MM/DD/YYYY h:mm AM/PM` in Eastern time.
 * Only strings with an explicit timezone offset are re-expressed in Eastern
 * time; a zone-less or date-only value is rendered verbatim. Falls back to
 * the raw string if the value is empty or unparseable.
 * @param {string} raw - The raw timestamp string from the BFF.
 * @returns {string} The formatted timestamp, or the raw string on failure.
 */
export function formatDatetime(raw: string): string {
  if (!raw) return "";
  if (!HAS_TIMEZONE.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const parts = new Map(EST_FORMAT.formatToParts(parsed).map((part) => [part.type, part.value]));
  return `${parts.get("month")}/${parts.get("day")}/${parts.get("year")} ${parts.get("hour")}:${parts.get("minute")} ${parts.get("dayPeriod")}`;
}

/**
 * Extracts the lower-cased username portion of an email-style identifier
 * (before `@`). Lower-cases the value unchanged when it isn't email-shaped.
 * @param {string} value - The raw identifier, e.g. `VKALUVAI@creditacceptance.com`.
 * @returns {string} The username, e.g. `vkaluvai`.
 */
export function usernameFromEmail(value: string): string {
  return value.split("@")[0].toLowerCase();
}

const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[^\s@]+/g;

// Bare usernames in these audit strings are alphanumeric (with `.`/`_`/`-`),
// never punctuation-heavy prose — guards the trailing-word lower-case below
// from firing on something that isn't actually a username.
const BARE_USERNAME_PATTERN = /^[A-Za-z0-9._-]+$/;

/**
 * Replaces any email-style identifiers embedded in free text (e.g. audit
 * strings like `Closed! 04/30/2026 vkaluvai@creditacceptance.com`) with just
 * their lower-cased username, then lower-cases the trailing word if it looks
 * like a bare (non-email) username — it's always the last token in these
 * audit strings.
 * @param {string} text - Free text that may contain one or more emails.
 * @returns {string} The text with embedded emails reduced to lower-cased usernames.
 */
export function usernamesOnly(text: string): string {
  const withoutEmails = text.replace(EMAIL_PATTERN, (email) => usernameFromEmail(email));
  const words = withoutEmails.split(" ");
  const lastWord = words.at(-1);
  if (lastWord && BARE_USERNAME_PATTERN.test(lastWord)) {
    words[words.length - 1] = lastWord.toLowerCase();
  }
  return words.join(" ");
}

/**
 * Converts a SCREAMING_SNAKE_CASE code to Title Case words.
 * e.g. `PRE_APPROVAL_GRANTED` → `Pre Approval Granted`
 * @param {string} code - The snake-case code string.
 * @returns {string} The title-cased display string.
 */
export function snakeToTitleCase(code: string): string {
  return code
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}
